import { createApi } from '@reduxjs/toolkit/query/react';
import axiosInstance from '../../services/api/axios';

import { ApiResponse } from '../../models/apiResponse.model';
import {
	GET_RECRUITING_MANAGERS,
	GET_DASHBOARD_DETAILS,
	GET_RECRUIT_DETAILS_BY_ID,
	GET_ROLES,
	GET_STAGES,
	GET_OFFICE_TYPES,
	GET_ALL_STATES,
	GET_PAYMENT_TYPES,
	GET_ALL_COMPANIES,
	GET_ALL_EXTERNAL_MANAGERS,
	GET_ALL_FIELDS,
	GET_FIELD_AUDIT_LOGS,
} from '../../services/api/apiEndpoints';
import { DropdownModel } from '../../models/dropdown.model';
import { DashboardResponse } from '../../models/responseModels.ts/dashboardResponse.model';
import { RecruitInfo } from '../../models/recruitInfo.model';
import { AuditLogListResponse } from '../../models/autitlog-grid-response.model';

export const recruitApiSlice = createApi({
	reducerPath: 'recruitApiSlice',
	baseQuery: async (arg) => {
		//, api, extraOptions
		const { baseURL, ...axiosConfig } = arg;
		try {
			const response = await axiosInstance.request({
				...axiosConfig,
			});
			return { data: response.data };
		} catch (error) {
			console.error(error);
			throw error;
		}
	},
	tagTypes: ['recruits'],
	endpoints: (builder) => ({
		getRoles: builder.query<ApiResponse<DropdownModel[]>, null>({
			query: () => ({
				url: `${GET_ROLES}`,
				method: 'GET',
			}),
		}),
		getStages: builder.query<ApiResponse<DropdownModel[]>, null>({
			query: () => ({
				url: `${GET_STAGES}`,
				method: 'GET',
			}),
		}),
		getRecruitingManagers: builder.query<
			ApiResponse<DropdownModel[]>,
			{ userEmail: string; isSysAdmin: boolean }
		>({
			query: ({ userEmail, isSysAdmin }) => ({
				url: `${GET_RECRUITING_MANAGERS}`,
				method: 'GET',
				params: {
					UserEmail: userEmail,
					IsSysAdmin: isSysAdmin,
				},
			}),
		}),
		getDashboardDetails: builder.mutation<
			ApiResponse<DashboardResponse>,
			{ recruiting_manager_ids: string[]; stage_id: string }
		>({
			query: (payload) => ({
				url: `${GET_DASHBOARD_DETAILS}`,
				method: 'POST',
				data: {
					...payload,
				},
			}),
		}),
		getRecruitDetailsById: builder.query<
			ApiResponse<RecruitInfo>,
			{ userId: string; userEmail: string }
		>({
			query: ({ userId, userEmail }) => ({
				url: `${GET_RECRUIT_DETAILS_BY_ID}`,
				method: 'GET',
				params: {
					recruitId: userId,
					userEmail,
				},
			}),
			keepUnusedDataFor: 0,
		}),
		getOfficeTypes: builder.query<ApiResponse<DropdownModel[]>, null>({
			query: () => ({
				url: `${GET_OFFICE_TYPES}`,
				method: 'GET',
			}),
		}),
		getStates: builder.query<ApiResponse<DropdownModel[]>, null>({
			query: () => ({
				url: `${GET_ALL_STATES}`,
				method: 'GET',
			}),
		}),
		getPaymentTypes: builder.query<ApiResponse<DropdownModel[]>, null>({
			query: () => ({
				url: `${GET_PAYMENT_TYPES}`,
				method: 'GET',
			}),
		}),
		getAllCompanies: builder.query<ApiResponse<DropdownModel[]>, null>({
			query: () => ({
				url: `${GET_ALL_COMPANIES}`,
				method: 'GET',
			}),
		}),
		getAllExternalManagers: builder.query<
			ApiResponse<DropdownModel[]>,
			null
		>({
			query: () => ({
				url: `${GET_ALL_EXTERNAL_MANAGERS}`,
				method: 'GET',
			}),
			keepUnusedDataFor: 0,
		}),
		getAllFields: builder.query<ApiResponse<DropdownModel[]>, null>({
			query: () => ({
				url: `${GET_ALL_FIELDS}`,
				method: 'GET',
			}),
		}),
		getAuditLogs: builder.mutation<
			ApiResponse<AuditLogListResponse>,
			{
				recruit_id: string;
				field_name: string;
				page_number: number;
				page_size: number;
			}
		>({
			query: (payload) => ({
				url: `${GET_FIELD_AUDIT_LOGS}`,
				method: 'POST',
				data: payload,
			}),
		}),
	}),
});

export const {
	useGetRolesQuery,
	useGetStagesQuery,
	useGetRecruitingManagersQuery,
	useGetDashboardDetailsMutation,
	useGetRecruitDetailsByIdQuery,
	useGetOfficeTypesQuery,
	useGetStatesQuery,
	useGetPaymentTypesQuery,
	useGetAllCompaniesQuery,
	useGetAllExternalManagersQuery,
	useGetAllFieldsQuery,
	useGetAuditLogsMutation,
} = recruitApiSlice;
